import { IconLeftBottomFoldedPaper } from 'assets/icons';
import { useIsMobile } from 'hooks/common';
import styled from 'styled-components';
import { UnfixedTicket } from 'types/api';
import { FixedTicket } from 'types/api';
import BookingCalendarV2 from './components/BookingCalendar.v2';
import { Option } from 'types/common.types';
import dayjs from 'dayjs';
import BookingCardV2 from './components/BookingCard.v2';
import { useMemo } from 'react';
import EmptyList from './components/EmptyList';

const BookingDateBox = styled.div<{ $isLast: boolean }>`
  display: flex;
  justify-content: flex-start;
  gap: 20px;

  padding-bottom: 24px;

  ${({ $isLast }) => `
      margin-bottom: ${$isLast ? '4px' : '24px'};
      border-bottom: ${$isLast ? 'none' : '1px solid'};
  `}

  border-color: rgba(234, 235, 236, 1);
`;

const DateBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;

  min-width: 31px;

  > p[data-type='day'] {
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;

    color: rgba(112, 115, 124, 1);
  }

  > p[data-type='date'] {
    font-weight: 600;
    font-size: 20px;
    line-height: 28px;

    color: rgba(0, 0, 0, 1);
  }

  > p[data-type='today'] {
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;

    color: rgba(0, 113, 233, 1);
  }
`;

const BookingCardListBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  width: 100%;
`;

interface BookingDateProps {
  date: string;
  value: (UnfixedTicket | FixedTicket)[];
  isLast: boolean;
  selectItemHandler: (item: UnfixedTicket | FixedTicket) => void;
}

const BookingDate = ({
  date,
  value,
  isLast,
  selectItemHandler,
}: BookingDateProps) => {
  const parsedDate = dayjs(date, 'YYYY년 M월 D일 dddd');
  const isToday = parsedDate.isSame(dayjs(), 'day');
  const dayOfMonth = parsedDate.format('D'); // "17"
  const dayOfWeek = parsedDate.format('ddd'); // "목"

  return (
    <BookingDateBox $isLast={isLast}>
      <DateBox>
        <p data-type="day">{dayOfWeek}</p>
        <p data-type="date">{dayOfMonth}</p>
        {isToday && <p data-type="today">오늘</p>}
      </DateBox>

      <BookingCardListBox>
        {value.map((item, idx) => (
          <BookingCardV2
            item={item}
            key={`${item.id}-${idx}`}
            selectItemHandler={selectItemHandler}
          />
        ))}
      </BookingCardListBox>
    </BookingDateBox>
  );
};

interface Props {
  bookingTickets: {
    date: string;
    value: (UnfixedTicket | FixedTicket)[];
  }[];
  selectItemHandler: (item: UnfixedTicket | FixedTicket) => void;
  selectedMonth: Option;
}

function BookingCalendarView({
  bookingTickets,
  selectItemHandler,
  selectedMonth,
}: Props) {
  const isMobile = useIsMobile();

  const mergedBookingTickets = useMemo(() => {
    /* 2025년 1월이 3번 나와서 이걸 한번에 모아주는 작업 */
    const mergedData = new Map<
      string,
      { date: string; value: (UnfixedTicket | FixedTicket)[] }
    >();

    bookingTickets.forEach(item => {
      if (mergedData.has(item.date)) {
        const existingData = mergedData.get(item.date)!;
        existingData.value = [...existingData.value, ...item.value];
      } else {
        mergedData.set(item.date, {
          date: item.date,
          value: [...item?.value],
        });
      }
    });

    return Array.from(mergedData.values()).sort((a, b) => {
      const aDate = dayjs(a.date, 'YYYY년 M월 D일 dddd');
      const bDate = dayjs(b.date, 'YYYY년 M월 D일 dddd');
      return aDate.isBefore(bDate) ? -1 : 1;
    });
  }, [bookingTickets]);

  return (
    <>
      {isMobile ? (
        bookingTickets.length === 0 ? (
          <EmptyList />
        ) : (
          mergedBookingTickets.map((item, idx) => {
            return (
              <BookingDate
                {...item}
                key={`${item.date}-${idx}`}
                selectItemHandler={selectItemHandler}
                isLast={idx === bookingTickets.length - 1}
              />
            );
          })
        )
      ) : (
        <BookingCalendarV2
          currentMonth={selectedMonth}
          bookingTickets={bookingTickets}
          selectItemHandler={selectItemHandler}
        />
      )}
    </>
  );
}

export default BookingCalendarView;
